import mapConfig, { mapImageLayerName } from "../lib/mapConfig";
import Basemap from "@arcgis/core/Basemap";
import TileLayer from "@arcgis/core/layers/TileLayer";
import WMTSLayer from "@arcgis/core/layers/WMTSLayer";
import MapImageLayer from "@arcgis/core/layers/MapImageLayer";
import request from "@arcgis/core/request";
import { basemapTypes } from "../lib/types";
import Graphic from "@arcgis/core/Graphic";
import Point from "@arcgis/core/geometry/Point";
import Geometry from "@arcgis/core/geometry/Geometry";
import Extent from "@arcgis/core/geometry/Extent";

interface LegendSecondaryItem {
  label: string;
  swatch: string;
}

export interface LegendItem {
  layerId: number;
  layerName: string;
  legendGroup: string;
  legendOrder: number;
  visible: boolean;
  minScale: number;
  maxScale: number;
  swatch: string;
  secondaryItems: LegendSecondaryItem[];
}

export const createBasemaps = (): any[] => {
  const bms = mapConfig.basemaps;
  return Object.keys(bms).map((basemapKey: string) => {
    let layer;
    if (bms[basemapKey].type === basemapTypes.esriTile) {
      layer = new TileLayer({
        url: bms[basemapKey].url,
      });
    } else if (bms[basemapKey].type === basemapTypes.wmts) {
      layer = new WMTSLayer({
        url: bms[basemapKey].url,
        activeLayer: {
          id: bms[basemapKey].layerId,
        },
        serviceMode: bms[basemapKey].serviceMode,
        opacity: bms[basemapKey].opacity,
        maxScale: bms[basemapKey].maxScale,
      });
    }
    return new Basemap({
      baseLayers: layer as any,
      title: bms[basemapKey].title,
      id: basemapKey,
      thumbnailUrl: bms[basemapKey].thumbnail,
    });
  });
};

export const getMapServiceInfo = (): Promise<any> => {
  return new Promise((resolve, reject) => {
    Promise.all([
      request(`${mapConfig.mapServiceConfig.url}/legend?f=json`, {
        responseType: "json",
      }),
      request(`${mapConfig.mapServiceConfig.url}/layers?f=json`, {
        responseType: "json",
      }),
    ]).then(
      ([res1, res2]) => {
        resolve([res1.data, res2.data]);
      },
      (reason: any) => {
        reject(reason);
      }
    );
  });
};

export const createMapImageLayer = (legendInfo: any) => {
  let sublayers: any[] = [];
  const visibleLayers: number[] = mapConfig.mapServiceConfig.visibleLayers;
  const opacities: Record<number, number> =
    mapConfig.mapServiceConfig.defaultOpacities;
  legendInfo.layers.forEach((layer: any) => {
    const layerId = layer.layerId;
    sublayers.push({
      id: layerId,
      visible: visibleLayers.includes(layerId),
      opacity: opacities[layerId] || 1,
      popupTemplate: mapConfig.popupConfig[layerId],
    });
  });
  const sortedSublayers = sublayers.sort((a, b) => b.id - a.id);
  return new MapImageLayer({
    id: mapImageLayerName,
    url: mapConfig.mapServiceConfig.url,
    sublayers: sortedSublayers,
  });
};

export const getMapSublayer = (layerId: number, map: any): any => {
  const layer = map.findLayerById(mapImageLayerName);
  const sublayer = layer.sublayers.getItemAt(
    layer.sublayers.findIndex((x: any) => x.id === layerId)
  );
  if (!sublayer) {
    throw new Error(
      `Unable to get sublayer for map service: ${mapImageLayerName} and layerId: ${layerId}`
    );
  }
  return sublayer;
};

export const isLegendItemVisible = (layerId: number, map: any): boolean => {
  return getMapSublayer(layerId, map).visible;
};

export const isLegendItemVisibleAtScale = (
  item: LegendItem,
  mapScale: number
): boolean => {
  if (item.minScale === 0 && item.maxScale === 0) {
    return true;
  }
  if (item.minScale !== 0 && item.minScale < mapScale) {
    return false;
  }
  if (item.maxScale !== 0 && item.maxScale > mapScale) {
    return false;
  }
  return true;
};

export const getLegendItemInfo = (lyr: any, legendList: any) => {
  const config = mapConfig.mapServiceConfig;
  let legendItems: any[] = [];
  if (config.singleItemGroupLayers.includes(lyr.id)) {
    // group layer, find all legend sub-items
    const sublayerIds = lyr.subLayers.map((x: any) => x.id);
    legendItems = legendList.filter((x: any) =>
      sublayerIds.includes(x.layerId)
    );
  } else {
    // only one legend item otherwise
    legendItems = legendList.filter((x: any) => x.layerId === lyr.id);
  }

  if (legendItems.length === 0) {
    throw new Error(`Unable to find legend information for layerId: ${lyr.id}`);
  } else if (legendItems.length === 1) {
    return legendItems[0];
  } else {
    const legendItem = { ...legendItems[0] };
    legendItem.minScale = Math.max(...legendItems.map((x: any) => x.minScale));
    legendItem.maxScale = Math.min(...legendItems.map((x: any) => x.maxScale));
    return legendItem;
  }
};

export const getLegendItemSublayers = (
  layerId: number,
  layersList: any,
  map: any
) => {
  const sublayers: any[] = [];
  if (mapConfig.mapServiceConfig.singleItemGroupLayers.includes(layerId)) {
    // get all sublayers of the group layer
    layersList
      .filter((x: any) => x.id === layerId)[0]
      .subLayers.forEach((x: any) => sublayers.push(getMapSublayer(x.id, map)));
  } else {
    sublayers.push(getMapSublayer(layerId, map));
  }
  return sublayers;
};

export const createLegendItems = (
  layersList: any,
  legendList: any,
  map: any
) => {
  const config = mapConfig.mapServiceConfig;
  const items: LegendItem[] = [];
  layersList.forEach((lyr: any) => {
    if (!lyr.parentLayer) return; // 1st level group layer
    if (config.singleItemGroupLayers.includes(lyr.parentLayer.id)) return; // These sublayers will be grouped
    const lgdInfo = getLegendItemInfo(lyr, legendList);
    const item = {
      layerId: lyr.id,
      layerName: lyr.name,
      legendGroup: lyr.parentLayer.name,
      legendOrder: config.legendItemOrder[lyr.id] || lyr.id,
      visible: isLegendItemVisible(lgdInfo.layerId, map),
      minScale: lgdInfo.minScale,
      maxScale: lgdInfo.maxScale,
      swatch: "",
      secondaryItems: [],
    };
    if (lgdInfo.legend.length === 1) {
      item.swatch = `data: ${lgdInfo.legend[0].contentType};base64,${lgdInfo.legend[0].imageData}`;
    } else {
      item.secondaryItems = lgdInfo.legend.map((x: any) => {
        return {
          label: x.label,
          swatch: `data: ${x.contentType};base64,${x.imageData}`,
        };
      });
    }
    items.push(item);
  });
  return items;
};

const getGeometryExtent = (geom: Geometry) => {
  if (geom.extent) return geom.extent;
  const extent = new Extent({
    xmin: 1,
    ymin: 1,
    xmax: 2,
    ymax: 2,
    spatialReference: geom.spatialReference,
  });
  return extent.centerAt(geom as Point);
};

export const getGraphicsExtent = (graphics: Graphic[]) => {
  let extent = getGeometryExtent(graphics[0].geometry).clone();
  graphics.slice(1).forEach((x: any) => {
    extent.union(getGeometryExtent(x.geometry));
  });
  return extent;
};
